import React from 'react';
import { Box, Typography, Alert, CircularProgress, Chip } from '@mui/material';
import { Warning, CheckCircle } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { fetchApi } from '@/api/fetchApi';

export interface ImpactAnalysisPanelProps {
  domainId: string;
  action: 'enable' | 'disable';
  tenantId: string;
}

interface ImpactAnalysis {
  domain: string;
  missingPrerequisites: string[];
  wouldDisable: string[];
  alreadyGranted: boolean;
}

export function ImpactAnalysisPanel({ domainId, action, tenantId }: ImpactAnalysisPanelProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['impact', tenantId, domainId, action],
    queryFn: () => fetchApi(`/domains/tenants/${tenantId}/entitlements/impact/${domainId}/${action}`),
    staleTime: 5 * 60 * 1000,
    enabled: !!domainId && !!tenantId,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">Analyzing impact...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load impact analysis.</Alert>;
  }

  const impact = data as ImpactAnalysis | undefined;
  if (!impact) return null;

  if (action === 'enable') {
    // Already granted
    if (impact.alreadyGranted) {
      return (
        <Alert severity="success" icon={<CheckCircle />} sx={{ my: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            This entitlement is already active for this tenant.
          </Typography>
        </Alert>
      );
    }

    // No missing prerequisites — ready to grant
    if (impact.missingPrerequisites.length === 0) {
      return (
        <Alert severity="success" icon={<CheckCircle />} sx={{ my: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Ready to grant — no prerequisites missing.
          </Typography>
        </Alert>
      );
    }

    // Has missing prerequisites
    return (
      <Alert severity="warning" sx={{ my: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
          The following must be granted first:
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {impact.missingPrerequisites.map((d) => (
            <Chip key={d} label={d} size="small" color="warning" variant="outlined" />
          ))}
        </Box>
      </Alert>
    );
  }

  // Disable action
  if (impact.wouldDisable.length === 0) {
    return (
      <Alert severity="info" sx={{ my: 1 }}>
        <Typography variant="body2">
          No dependent domains will be affected.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box sx={{ my: 1 }}>
      <Alert severity="warning" icon={<Warning />}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
          Disabling {domainId} will also disable:
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {impact.wouldDisable.map((d) => (
            <Chip key={d} label={d} size="small" color="error" variant="outlined" />
          ))}
        </Box>
      </Alert>
    </Box>
  );
}
